import { useNavigate } from 'react-router-dom';
import type { MyResumeItem } from './useMinisterAccess';

interface GuideStep {
  key: 'resume' | 'intro' | 'portfolio';
  title: string;
  description: string;
  done: boolean;
  locked: boolean;
  actionLabel: string;
  onAction: () => void;
}

// 사역자 회원에게 본인 이력서 작성 여부를 보여주고
// 이력서 → 자기소개서 → 사역 포트폴리오 작성을 유도한다 (모두 완료하면 숨김)
export default function MinisterResumeGuide({ resumeList }: { resumeList: MyResumeItem[] }) {
  const navigate = useNavigate();

  const go = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  // 공개 동의한 이력서를 우선 기준으로 삼는다 (목록은 updatedAt DESC)
  const mainResume = resumeList.find((item) => !!Number(item.isPublic)) || resumeList[0];
  const hasResume = !!mainResume;
  const isPublic = hasResume && !!Number(mainResume.isPublic);
  const editPath = hasResume ? `/mypage/resumeedit?id=${mainResume.id}` : '/mypage/resumeedit';

  const steps: GuideStep[] = [
    {
      key: 'resume',
      title: '이력서',
      description: hasResume
        ? isPublic
          ? '이력서가 교회 담당자에게 공개되어 있습니다.'
          : '이력서가 비공개 상태입니다. 공개 동의를 해야 교회 담당자가 열람할 수 있어요.'
        : '학력·경력·희망사항을 입력해 이력서를 작성해주세요.',
      done: hasResume && isPublic,
      locked: false,
      actionLabel: hasResume ? (isPublic ? '이력서 수정' : '공개 설정하기') : '이력서 작성하기',
      onAction: () => go(editPath),
    },
    {
      key: 'intro',
      title: '자기소개서',
      description: '소명과 사역 비전, 강점을 소개해 교회 담당자에게 나를 알려주세요.',
      done: !!mainResume?.hasSelfIntroduction,
      locked: !hasResume,
      actionLabel: mainResume?.hasSelfIntroduction ? '자기소개서 수정' : '자기소개서 작성하기',
      onAction: () => go(`${editPath}&section=intro`),
    },
    {
      key: 'portfolio',
      title: '사역 포트폴리오',
      description: '설교영상·찬양영상·사역사진을 등록하면 교회 담당자의 관심을 더 받을 수 있어요.',
      done: (mainResume?.portfolioCount ?? 0) > 0,
      locked: !hasResume,
      actionLabel: (mainResume?.portfolioCount ?? 0) > 0 ? '포트폴리오 수정' : '포트폴리오 등록하기',
      onAction: () => go(`${editPath}&section=portfolio`),
    },
  ];

  const doneCount = steps.filter((step) => step.done).length;
  const allDone = doneCount === steps.length;
  if (allDone) return null;

  return (
    <div className="minister-info__resume-guide">
      <div className="minister-info__resume-guide-head">
        <h3>
          {hasResume ? '사역자정보를 완성해주세요' : '아직 등록된 이력서가 없습니다'}
        </h3>
        <p>
          {hasResume
            ? '자기소개서와 포트폴리오까지 작성하면 교회 담당자에게 먼저 연락받을 수 있어요.'
            : '이력서를 작성하면 교회 담당자에게 연락받을 수 있어요.'}
        </p>
        <div className="minister-info__resume-guide-progress">
          <span>{doneCount} / {steps.length} 완료</span>
          <div className="minister-info__resume-guide-bar">
            <div style={{ width: `${(doneCount / steps.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <ol className="minister-info__resume-guide-steps">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className={`minister-info__resume-guide-step${step.done ? ' is-done' : ''}${step.locked ? ' is-locked' : ''}`}
          >
            <div className="minister-info__resume-guide-num">{step.done ? '✓' : index + 1}</div>
            <div className="minister-info__resume-guide-body">
              <h4>
                {step.title}
                <em>{step.done ? '완료' : step.locked ? '이력서 작성 후 가능' : '미작성'}</em>
              </h4>
              <p>{step.description}</p>
            </div>
            <button
              type="button"
              className="minister-info__guard-btn"
              disabled={step.locked}
              onClick={step.onAction}
            >
              {step.actionLabel}
            </button>
          </li>
        ))}
      </ol>

      {hasResume && (
        <div className="minister-info__resume-guide-foot">
          <button type="button" onClick={() => go('/mypage/resumemanage')}>
            내 이력서 관리로 이동
          </button>
        </div>
      )}
    </div>
  );
}
